import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'

import { useGetOrderQuery } from '@/app/services/orders'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn, setOpenGuestLoginDialog } from '@/app/slices/guestUserSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { Paths } from '@/common/utils'
import { ORDER_PAYMENT_STATUS, ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

/**
 * useOrderNavigation - Manages order page navigation and authentication
 *
 * Controls navigation flow across order-related pages based on order status,
 * user authentication, and business rules. Prevents unauthorized access and
 * ensures users follow the correct order flow:
 * Order (Editable) → Review (Draft) → Checkout (Issued) → Confirmation (Ordered) → Payment Success (Paid)
 * Payment Success also accepts Issued/Unpaid orders, because Stripe redirects there before its webhook runs.
 *
 * **REFACTORED** (2025-01-17): Changed return type to support loading UI rendering
 *
 * @param {'order'|'review'|'checkout'|'confirmation'|'paymentSuccess'} page - Current page identifier
 *   - `'order'` - Main order editing page (editable/calculated orders only)
 *   - `'review'` - Order review page (draft orders only)
 *   - `'checkout'` - Stripe checkout page (issued orders only)
 *   - `'confirmation'` - Order confirmation page (ordered/invoiced orders)
 *   - `'paymentSuccess'` - Payment success page (paid orders, or issued orders waiting for the Stripe webhook)
 *
 * @returns {{isNavigating: boolean, to: React.ReactElement|null}} Navigation state object
 *   - `isNavigating` - True if redirecting, false if allowing component to render
 *   - `to` - Navigate component if redirecting, null otherwise
 *
 * @example
 * // Current usage (post-refactor)
 * const { isNavigating, to } = useOrderNavigation('order')
 * if (isNavigating) return to  // Redirect when needed
 * return <OrderContent />       // Render normally (including loading states)
 *
 * @example
 * // Old usage (deprecated - for reference only)
 * const navigation = useOrderNavigation('order')
 * if (navigation) return navigation  // ❌ Blocks loading UI
 *
 * @see {@link docs/USE_ORDER_NAVIGATION_REFACTOR_INVESTIGATION.md} - Full refactor documentation
 * @see {@link src/features/order/Order.jsx} - Example usage in Order component
 * @see {@link src/features/order-review/OrderReview.jsx} - Example usage in OrderReview component
 */
export const useOrderNavigation = (page = 'order') => {
    const { orderId } = useParams()
    const dispatch = useDispatch()
    const { isAuthenticated, login } = useKindeAuth()

    const currentCustomerId = useSelector(selectCustomerId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)
    const organisationId = useSelector(selectOrganisationId)
    // Fixed: Removed duplicate selector call (was calling selectOrganisation twice)
    const organisation = useSelector(selectOrganisation)

    const {
        data: order,
        error,
        isError,
        isLoading,
    } = useGetOrderQuery(
        {
            organisationId,
            orderId,
        },
        {
            skip: !organisationId || !orderId,
        }
    )

    const orderStatus = useMemo(
        () => ({
            isEditable: [ORDER_STATUS_ARRAY.NotCalculated, ORDER_STATUS_ARRAY.Calculated].includes(order?.status),
            isDraft: order?.status === ORDER_STATUS_ARRAY.Draft,
            isIssued: order?.status === ORDER_STATUS_ARRAY.Issued,
            isOrdered: [
                ORDER_STATUS_ARRAY.PendingOrderConfirmation,
                ORDER_STATUS_ARRAY.Ordered,
                ORDER_STATUS_ARRAY.Invoiced,
            ].includes(order?.status),
            isPaid: [ORDER_PAYMENT_STATUS.GatewayPaid, ORDER_PAYMENT_STATUS.ManualPaid].includes(order?.paymentStatus),
        }),
        [order?.status, order?.paymentStatus]
    )

    /**
     * Handles custom order confirmation URL redirect
     *
     * ⚠️ WARNING: This function has a side effect - it redirects the browser
     * via window.location.assign() before returning. Component state may not
     * be cleaned up before redirect occurs.
     *
     * @returns {boolean} True if custom URL exists and redirect triggered, false otherwise
     */
    const handleCustomConfirmation = useCallback(() => {
        if (organisation?.webStoreCustomOrderConfirmationUrl) {
            window.location.assign(organisation.webStoreCustomOrderConfirmationUrl)
            return true
        }
        return false
    }, [organisation?.webStoreCustomOrderConfirmationUrl])

    // Early returns for loading and error states
    if (isLoading) return { isNavigating: false, to: null }
    if (!organisation?.webStoreIsPublic && !isAuthenticated) {
        return { isNavigating: true, to: <Navigate to={Paths.HOME_PATHNAME} /> }
    }
    if (isError) {
        // Type guard: Check if error has 'status' property (FetchBaseQueryError)
        if (error && 'status' in error && typeof error.status === 'number' && error.status >= 500) {
            return { isNavigating: true, to: <Navigate to={Paths.ERROR_PATHNAME} /> }
        }
        return { isNavigating: true, to: <Navigate to={Paths.NOT_FOUND} /> }
    }
    if (!order || Object.keys(order).length === 0) {
        return { isNavigating: true, to: <Navigate to={Paths.NOT_FOUND} /> }
    }

    // Authentication checks
    if (order?.customerContactId) {
        // Check if authenticated user is accessing the correct customer order
        if (
            isAuthenticated &&
            currentCustomerId !== order?.customerContact?.customerId &&
            currentCustomerId !== order?.customerId
        ) {
            return { isNavigating: true, to: <Navigate to={Paths.NOT_FOUND} /> }
        }

        // Check if guest user is accessing the correct customer order
        if (
            isGuestLoggedIn &&
            guestUser?.customerId !== order?.customerContact?.customerId &&
            guestUser?.customerId !== order?.customerId
        ) {
            return { isNavigating: true, to: <Navigate to={Paths.NOT_FOUND} /> }
        }

        // Handle unauthenticated users
        if (!isAuthenticated && !isGuestLoggedIn) {
            // If user has a Kinde account, redirect to login
            if (order?.customerContact?.isKindeUser) {
                login({
                    orgCode: organisation?.kindeOrganisationId,
                    state: { redirectTo: window.location.href },
                })
                // Custom redirect via login() - allow component to render during redirect
                return { isNavigating: false, to: null }
            }

            // For non-Kinde users, open guest login dialog
            dispatch(setOpenGuestLoginDialog(page))
        }
    }

    // Page-specific navigation logic
    const { isDraft, isEditable, isIssued, isOrdered, isPaid } = orderStatus

    if (page === 'order') {
        if (isDraft) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
        if (isIssued) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
        if (isOrdered && handleCustomConfirmation()) {
            return { isNavigating: false, to: null }
        }
        if (isOrdered) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
    }

    if (page === 'review') {
        if (isEditable) {
            return { isNavigating: true, to: <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} /> }
        }
        if (isIssued) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
        if (isOrdered && handleCustomConfirmation()) {
            return { isNavigating: false, to: null }
        }
        if (isOrdered) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
    }

    if (page === 'checkout') {
        if (isEditable) {
            return { isNavigating: true, to: <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} /> }
        }
        if (isOrdered && handleCustomConfirmation()) {
            return { isNavigating: false, to: null }
        }
        if (isOrdered) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
    }

    if (page === 'confirmation') {
        if (!isOrdered) {
            if (isEditable) {
                return { isNavigating: true, to: <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} /> }
            }
            if (isDraft) {
                return {
                    isNavigating: true,
                    to: <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />,
                }
            }
            if (isIssued) {
                return {
                    isNavigating: true,
                    to: <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />,
                }
            }
        }
        if (isPaid) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_PAYMENT_SUCCESS.replace(':orderId', order.id)} />,
            }
        }
        if (handleCustomConfirmation()) {
            return { isNavigating: false, to: null }
        }
    }

    if (page === 'paymentSuccess') {
        // Stripe sends the browser here before its webhook has necessarily marked the
        // order as paid. An issued or unpaid order must stay on this page and wait;
        // redirecting it back to checkout led customers to pay a second time.
        if (isEditable) {
            return { isNavigating: true, to: <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} /> }
        }
        if (isDraft) {
            return {
                isNavigating: true,
                to: <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />,
            }
        }
    }

    return { isNavigating: false, to: null }
}
